/**
 * Helper puri di formattazione, derivazione e organizzazione dati per la
 * dashboard ESXi. Nessuna logica di rete: solo trasformazioni testabili.
 */

import {
  ENVIRONMENT_ORDER,
  NO_ENVIRONMENT_LABEL,
  NO_PRODUCT_LABEL,
  POWER_STATE,
  identityStatusMeta,
  isSupportedAction,
  normalizePowerState,
} from './constants';

// --- Formattazione unità -----------------------------------------------------

/** Uptime in secondi → stringa compatta (es. "16g 4h", "24h 10m", "5m"). */
export const formatUptime = (seconds) => {
  if (seconds == null || Number.isNaN(Number(seconds)) || Number(seconds) < 0) {
    return '—';
  }
  const s = Math.floor(Number(seconds));
  if (s < 60) return `${s}s`;
  const mins = Math.floor(s / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hours < 24) return `${hours}h ${remMins}m`;
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return `${days}g ${remHours}h`;
};

/** Megabyte → stringa leggibile (MB fino a 1024, poi GB). Robusto ai null. */
export const formatMemoryMb = (mb) => {
  if (mb == null || Number.isNaN(Number(mb))) return '—';
  const v = Number(mb);
  if (v < 1024) return `${Math.round(v)} MB`;
  return `${(v / 1024).toFixed(1)} GB`;
};

/** MHz → stringa leggibile (MHz fino a 1000, poi GHz). */
export const formatMhz = (mhz) => {
  if (mhz == null || Number.isNaN(Number(mhz))) return '—';
  const v = Number(mhz);
  if (v < 1000) return `${Math.round(v)} MHz`;
  return `${(v / 1000).toFixed(2)} GHz`;
};

/** Data ISO / stringa → orario locale italiano, robusto ai null. */
export const formatDateTime = (value) => {
  if (!value) return '—';
  const iso = typeof value === 'string' ? value.replace(' ', 'T') : value;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

/** "Tempo fa" sintetico da un timestamp al momento della chiamata. */
export const formatRelativeAge = (value, now = Date.now()) => {
  if (!value) return '—';
  const iso = typeof value === 'string' ? value.replace(' ', 'T') : value;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const secs = Math.max(0, Math.round((now - t) / 1000));
  if (secs < 5) return 'adesso';
  if (secs < 60) return `${secs}s fa`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m fa`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h fa`;
  const days = Math.floor(hours / 24);
  return `${days}g fa`;
};

/** Percentuale intera 0..100 protetta da divisione per zero / valori assurdi. */
export const percent = (used, total) => {
  const u = Number(used);
  const t = Number(total);
  if (!Number.isFinite(u) || !Number.isFinite(t) || t <= 0) return null;
  return Math.max(0, Math.min(100, Math.round((u / t) * 100)));
};

// --- Accesso difensivo -------------------------------------------------------

/**
 * Legge il primo valore definito tra più chiavi candidate di un oggetto.
 * Serve a consumare il summary host senza fissare nomi di campo non
 * documentati: se nessuna chiave è presente si ottiene `undefined`, mai un
 * valore inventato.
 */
export const pick = (obj, keys, fallback = undefined) => {
  if (!obj) return fallback;
  for (const k of keys) {
    const v = obj[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return fallback;
};

/** Lista prodotti sempre come array di stringhe non vuote. */
export const vmProducts = (vm) => {
  const raw = vm?.products;
  if (Array.isArray(raw)) return raw.filter((p) => p != null && p !== '');
  if (raw) return [String(raw)];
  return [];
};

/** Ambiente della VM, con etichetta di ripiego. */
export const vmEnvironment = (vm) => {
  const env = vm?.environment;
  return env && String(env).trim() ? String(env).trim() : NO_ENVIRONMENT_LABEL;
};

/**
 * Restituisce l'indirizzo più utile da mostrare per una VM.
 *
 * VMware espone l'IP guest soltanto quando la VM è accesa e VMware Tools lo
 * comunica. Il catalogo contiene invece gli indirizzi attesi, disponibili
 * anche a VM spenta. La provenienza resta esplicita per non presentare un dato
 * di configurazione come se fosse stato osservato in tempo reale.
 */
export const vmIpInfo = (vm) => {
  const runtimeIp =
    typeof vm?.guest?.ipAddress === 'string'
      ? vm.guest.ipAddress.trim()
      : '';
  const expectedIps = (Array.isArray(vm?.expectedIps) ? vm.expectedIps : [])
    .map((ip) => String(ip || '').trim())
    .filter(Boolean);

  if (runtimeIp) {
    return {
      primary: runtimeIp,
      source: 'runtime',
      expectedIps,
      mismatch: expectedIps.length > 0 && !expectedIps.includes(runtimeIp),
    };
  }
  if (expectedIps.length > 0) {
    return {
      primary: expectedIps[0],
      source: 'catalog',
      expectedIps,
      mismatch: false,
    };
  }
  return {
    primary: null,
    source: null,
    expectedIps: [],
    mismatch: false,
  };
};

// --- Identità e azioni -------------------------------------------------------

/**
 * Una VM è ambigua (niente pulsanti operativi) se il suo `identityStatus` non è
 * MATCHED, oppure se compare tra gli avvisi di catalogo per UUID duplicati o
 * nomi non coerenti.
 */
export const isVmIdentityAmbiguous = (vm, catalogWarnings) => {
  if (!vm) return true;
  if (identityStatusMeta(vm.identityStatus).ambiguous) return true;
  const w = catalogWarnings || {};
  const inList = (list) => {
    if (!Array.isArray(list)) return false;
    return list.some((entry) => {
      if (entry == null) return false;
      if (typeof entry === 'string') {
        return entry === vm.catalogId || entry === vm.name;
      }
      return (
        entry.catalogId === vm.catalogId ||
        entry.name === vm.name ||
        entry.expectedName === vm.name
      );
    });
  };
  return inList(w.duplicateUuids) || inList(w.nameMismatches);
};

/**
 * Azioni operative effettivamente mostrabili per una VM:
 * - solo quelle presenti in `availableActions`;
 * - solo azioni supportate dal contratto (powerOn/shutdown);
 * - nessuna azione se l'identità è ambigua.
 */
export const visibleActionsForVm = (vm, catalogWarnings) => {
  if (!vm) return [];
  if (isVmIdentityAmbiguous(vm, catalogWarnings)) return [];
  const actions = Array.isArray(vm.availableActions) ? vm.availableActions : [];
  return actions.filter(isSupportedAction);
};

// --- Conteggi e distribuzione ------------------------------------------------

/**
 * Conteggio VM per stato di alimentazione, calcolato dagli item documentati.
 * Rappresenta la fonte autorevole quando il summary host non espone i conteggi.
 */
export const countByPowerState = (vms = []) => {
  const counts = {
    [POWER_STATE.ON]: 0,
    [POWER_STATE.OFF]: 0,
    [POWER_STATE.SUSPENDED]: 0,
    [POWER_STATE.UNKNOWN]: 0,
    total: 0,
  };
  for (const vm of vms) {
    const s = normalizePowerState(vm?.powerState);
    counts[s] += 1;
    counts.total += 1;
  }
  return counts;
};

// --- Filtri ------------------------------------------------------------------

/** Valori distinti (ordinati) di un attributo per popolare le tendine filtro. */
export const distinctValues = (vms, accessor) => {
  const set = new Set();
  for (const vm of vms || []) {
    const v = accessor(vm);
    if (Array.isArray(v)) v.forEach((x) => x && set.add(x));
    else if (v != null && v !== '') set.add(v);
  }
  return Array.from(set).sort((a, b) => String(a).localeCompare(String(b), 'it'));
};

/** Opzioni distinte per i filtri, derivate dall'inventario corrente. */
export const buildFilterOptions = (vms = []) => ({
  environments: distinctValues(vms, (vm) => vmEnvironment(vm)),
  products: distinctValues(vms, (vm) => vmProducts(vm)),
  roles: distinctValues(vms, (vm) => vm.role),
  states: distinctValues(vms, (vm) => normalizePowerState(vm.powerState)),
});

const matchesSearch = (vm, term) => {
  if (!term) return true;
  const q = term.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    vm.name,
    vm.role,
    vm.description,
    vm.guest?.ipAddress,
    vm.guest?.hostName,
    ...(Array.isArray(vm.expectedIps) ? vm.expectedIps : []),
    ...vmProducts(vm),
  ]
    .filter(Boolean)
    .map((s) => String(s).toLowerCase());
  return haystack.some((s) => s.includes(q));
};

/**
 * Applica i filtri a un elenco di VM.
 * @param {Object} filters { search, environment, product, role, state }
 */
export const filterVms = (vms = [], filters = {}) => {
  const { search, environment, product, role, state } = filters;
  return vms.filter((vm) => {
    if (!matchesSearch(vm, search)) return false;
    if (environment && vmEnvironment(vm) !== environment) return false;
    if (product && !vmProducts(vm).includes(product)) return false;
    if (role && vm.role !== role) return false;
    if (state && normalizePowerState(vm.powerState) !== state) return false;
    return true;
  });
};

export const hasActiveFilters = (filters = {}) =>
  Boolean(
    (filters.search && filters.search.trim()) ||
      filters.environment ||
      filters.product ||
      filters.role ||
      filters.state,
  );

export const EMPTY_FILTERS = {
  search: '',
  environment: '',
  product: '',
  role: '',
  state: '',
};

// --- Raggruppamento ----------------------------------------------------------

const environmentRank = (env) => {
  const idx = ENVIRONMENT_ORDER.indexOf(String(env).toUpperCase());
  if (idx >= 0) return idx;
  if (env === NO_ENVIRONMENT_LABEL) return ENVIRONMENT_ORDER.length + 1;
  return ENVIRONMENT_ORDER.length; // altri ambienti, prima di "senza ambiente"
};

/** Bucket di prodotto per una VM: il primo prodotto, o etichetta di ripiego. */
export const primaryProduct = (vm) => {
  const products = vmProducts(vm);
  return products.length > 0 ? products[0] : NO_PRODUCT_LABEL;
};

/**
 * Raggruppa le VM prima per ambiente e poi per prodotto.
 *
 * Una VM multiprodotto NON viene duplicata: appartiene al bucket del suo primo
 * prodotto, mentre tutti i suoi prodotti restano visibili come badge sulla
 * scheda/riga. Questo evita raggruppamenti ambigui pur onorando l'appartenenza
 * multipla.
 *
 * @returns Array<{ environment, count, products: Array<{ product, vms }> }>
 */
export const groupVms = (vms = []) => {
  const byEnv = new Map();
  for (const vm of vms) {
    const env = vmEnvironment(vm);
    if (!byEnv.has(env)) byEnv.set(env, new Map());
    const prodBucket = byEnv.get(env);
    const prod = primaryProduct(vm);
    if (!prodBucket.has(prod)) prodBucket.set(prod, []);
    prodBucket.get(prod).push(vm);
  }

  const envs = Array.from(byEnv.entries()).map(([environment, prodMap]) => {
    const products = Array.from(prodMap.entries())
      .map(([product, groupVmsList]) => ({ product, vms: groupVmsList }))
      .sort((a, b) => a.product.localeCompare(b.product, 'it'));
    const count = products.reduce((sum, p) => sum + p.vms.length, 0);
    return { environment, count, products };
  });

  envs.sort((a, b) => {
    const ra = environmentRank(a.environment);
    const rb = environmentRank(b.environment);
    if (ra !== rb) return ra - rb;
    return a.environment.localeCompare(b.environment, 'it');
  });

  return envs;
};

// --- Summary host difensivo --------------------------------------------------

/**
 * Normalizza la risposta `/summary` in una forma stabile per la UI, senza
 * fissare nomi di campo non documentati. I conteggi VM ricadono sui dati
 * `/vms` (contratto documentato) quando il summary non li espone. I valori
 * host non presenti restano `null` e la UI li mostra come "non determinato".
 */
export const normalizeSummary = (summary, vms = []) => {
  const s = summary || {};
  const host = s.host || s;
  const hostCpu = host.cpu || {};
  const hostMemory = host.memory || {};
  const counts = countByPowerState(vms);

  const readCount = (keys, fallback) => {
    const v = pick(s.vmCounts || s.counts || s, keys);
    return v == null ? fallback : Number(v);
  };

  const cpuUsageMhz =
    pick(hostCpu, ['usageMhz', 'usedMhz', 'overallCpuUsage']) ??
    pick(host, ['cpuUsageMhz', 'cpuUsedMhz']);
  const explicitCpuTotalMhz =
    pick(hostCpu, ['totalMhz', 'capacityMhz']) ??
    pick(host, ['cpuTotalMhz', 'cpuCapacityMhz']);
  const mhzPerCore = pick(hostCpu, ['mhzPerCore', 'cpuMhz']);
  const cpuCores = pick(hostCpu, ['cores', 'numCpuCores']);
  const derivedCpuTotalMhz =
    Number.isFinite(Number(mhzPerCore)) && Number.isFinite(Number(cpuCores))
      ? Number(mhzPerCore) * Number(cpuCores)
      : null;
  const cpuTotalMhz = explicitCpuTotalMhz ?? derivedCpuTotalMhz;
  const cpuUsagePct =
    pick(host, ['cpuUsagePercent', 'cpuUsagePct', 'cpuPercent']) ??
    percent(cpuUsageMhz, cpuTotalMhz);

  const memoryUsedMb =
    pick(hostMemory, ['usageMb', 'usedMb', 'overallMemoryUsage']) ??
    pick(host, ['memoryUsedMb', 'memoryUsageMb', 'usedMemoryMb']);
  const memoryTotalBytes = pick(hostMemory, ['totalBytes', 'memorySize']);
  const memoryTotalMb =
    pick(hostMemory, ['totalMb', 'capacityMb']) ??
    pick(host, ['memoryTotalMb', 'memoryCapacityMb', 'totalMemoryMb']) ??
    (Number.isFinite(Number(memoryTotalBytes))
      ? Number(memoryTotalBytes) / (1024 * 1024)
      : null);
  const memoryUsagePct =
    pick(host, ['memoryUsagePercent', 'memoryUsagePct', 'memPercent']) ??
    percent(memoryUsedMb, memoryTotalMb);

  return {
    hostId: pick(s, ['hostId']) ?? pick(host, ['hostId', 'id']) ?? null,
    // connectionState: "connected"/"disconnected"/"notResponding"/… oppure null.
    connectionState:
      pick(host, ['connectionState', 'connectionStatus', 'reachability']) ??
      (s.reachable === false ? 'disconnected' : null),
    reachable: pick(s, ['reachable', 'hostReachable']) ?? null,
    generatedAt: pick(s, ['generatedAt', 'updatedAt', 'timestamp']) ?? null,
    stale: Boolean(pick(s, ['stale']) ?? false),
    warning: pick(s, ['warning']) ?? null,
    configured: pick(s, ['configured']) ?? null,
    maintenanceMode: pick(host, ['maintenanceMode', 'inMaintenanceMode']) ?? null,
    rebootRequired: pick(host, ['rebootRequired', 'restartRequired']) ?? null,
    uptimeSeconds:
      pick(host, ['uptimeSeconds', 'hostUptimeSeconds', 'uptime']) ?? null,
    cpuUsagePct: cpuUsagePct ?? null,
    cpuUsageMhz: cpuUsageMhz ?? null,
    cpuTotalMhz: cpuTotalMhz ?? null,
    memoryUsagePct: memoryUsagePct ?? null,
    memoryUsedMb: memoryUsedMb ?? null,
    memoryTotalMb: memoryTotalMb ?? null,
    catalogWarnings: s.catalogWarnings || null,
    // Conteggi: preferisci il summary, ricadi sui dati /vms.
    counts: {
      total: readCount(['total', 'vmTotal'], counts.total),
      on: readCount(['on', 'poweredOn'], counts.ON),
      off: readCount(['off', 'poweredOff'], counts.OFF),
      suspended: readCount(['suspended'], counts.SUSPENDED),
      unknown: readCount(['unknown'], counts.UNKNOWN),
    },
  };
};

// --- Lock operazioni ---------------------------------------------------------

/**
 * Applica subito all'inventario locale lo stato finale già verificato dal
 * backend. Non inventa metriche runtime: dopo un power-on restano non
 * determinate fino alla lettura live; dopo lo shutdown vengono azzerate.
 *
 * Lo shutdown non viene reso cliccabile subito dopo un'accensione perché
 * richiede una nuova conferma di VMware Tools dal backend.
 */
export const reconcileVmAfterOperation = (vm, operation) => {
  if (!vm || operation?.status !== 'SUCCEEDED') return vm;

  const target = normalizePowerState(
    operation.observedPowerState || operation.targetPowerState,
  );
  if (target !== POWER_STATE.ON && target !== POWER_STATE.OFF) return vm;

  const guest = { ...(vm.guest || {}) };
  const resources = { ...(vm.resources || {}) };

  if (target === POWER_STATE.OFF) {
    guest.hostName = null;
    guest.ipAddress = null;
    guest.toolsStatus = 'toolsNotRunning';
    guest.toolsRunningStatus = 'guestToolsNotRunning';
    guest.heartbeatStatus = 'gray';
    resources.cpuUsageMhz = null;
    resources.guestMemoryUsageMb = null;
    resources.hostMemoryUsageMb = null;
    resources.uptimeSeconds = null;
  } else {
    // I dati della VM spenta non diventano magicamente validi all'accensione:
    // sarà il refresh live a popolare IP, Tools, consumi e uptime.
    guest.toolsStatus = null;
    guest.toolsRunningStatus = null;
    guest.heartbeatStatus = 'gray';
    resources.cpuUsageMhz = null;
    resources.guestMemoryUsageMb = null;
    resources.hostMemoryUsageMb = null;
    resources.uptimeSeconds = null;
  }

  return {
    ...vm,
    powerState: target,
    guest,
    resources,
    // OFF può essere riaccesa subito. ON attende il riscontro di VMware Tools
    // prima di proporre lo shutdown guest.
    availableActions: target === POWER_STATE.OFF ? ['powerOn'] : [],
  };
};

/**
 * True se la VM ha un'operazione non ancora conclusa (lock attivo). È la base
 * della prevenzione dei doppi click: finché il lock è attivo non si accodano
 * nuove azioni per la stessa VM.
 */
export const isVmLocked = (operations, catalogId) => {
  const op = operations && operations[catalogId];
  return Boolean(op && op.phase !== 'done');
};

/** Numero totale di anomalie presenti nei catalogWarnings, 0 se assenti. */
export const catalogWarningCount = (catalogWarnings) => {
  if (!catalogWarnings) return 0;
  const lists = [
    catalogWarnings.uncatalogued,
    catalogWarnings.missing,
    catalogWarnings.duplicateUuids,
    catalogWarnings.nameMismatches,
  ];
  return lists.reduce(
    (sum, l) => sum + (Array.isArray(l) ? l.length : 0),
    0,
  );
};
